/**
 * Autopay — who pays the month-end bill with the card on file.
 *
 * Owner, Oct 2 2026: "add a feature on billing to tell me who has auto pay."
 * Settings → Billing had only the counts, the customer's own page has the
 * switch, and nothing on Billing said who. This card is the names, in three
 * groups (server/services/autopay customers()): charged — chose autopay and
 * has a card at Stripe, the only ones ever charged; card saved but autopay
 * off — they get the normal bill; autopay on but no card — cannot be charged.
 * A name opens the profile, a decline can be charged again here, and the
 * office can turn one customer on or off (on needs a card on file).
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ChevronDown, CreditCard } from 'lucide-react';
import { api } from '../services/api';
import { fmtTs } from '../utils/time';

interface AutopayPerson {
  customer_id: number; name: string; phone: string | null;
  autopay_enabled: boolean; payment_method_on_file: boolean;
  card_brand: string | null; card_last4: string | null;
  card_checked: boolean | null;   // true: Stripe answered · false: the ask failed · null: not asked (practice / no Stripe)
  last_charge: { amount: number; paid_at: string; invoice_number: string } | null;
  open_decline: { id: number; invoice_number: string; total: number; balance: number; error: string } | null;
}
interface AutopayWho {
  switch_on: boolean; stripe: string; practice: boolean; charging: boolean; words: string;
  charged: AutopayPerson[]; card_only: AutopayPerson[]; no_card: AutopayPerson[];
}

const money = (n: any) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// DB timestamps are UTC; show the Austin day, whatever the device is set to.
const day = (ts?: string | null) => fmtTs(ts, { month: 'short', day: 'numeric' });
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
const cardWords = (p: AutopayPerson) => {
  if (!p.payment_method_on_file) return 'No card at Stripe';
  const brand = p.card_brand ? p.card_brand.charAt(0).toUpperCase() + p.card_brand.slice(1) : 'Card';
  const base = !p.card_brand && !p.card_last4 ? 'Card on file' : `${brand}${p.card_last4 ? ` ···· ${p.card_last4}` : ''}`;
  return p.card_checked === false ? `${base} (could not check Stripe just now)` : base;
};
// Open the first time so he sees the names; after that, the way he left it (this device only).
const OPEN_KEY = 'billing_autopay_open';
const readOpen = () => { try { const v = localStorage.getItem(OPEN_KEY); return v === null ? true : v === '1'; } catch { return true; } };

export default function AutopayCard() {
  const qc = useQueryClient();
  const [open, setOpenState] = useState<boolean>(readOpen);
  const setOpen = (fn: (o: boolean) => boolean) => setOpenState(o => { const n = fn(o); try { localStorage.setItem(OPEN_KEY, n ? '1' : '0'); } catch { /* private window: just this visit */ } return n; });
  const [showCardOnly, setShowCardOnly] = useState(false);
  const [showNoCard, setShowNoCard] = useState(true);

  const { data } = useQuery('billing-autopay-who',
    () => api.get('/billing/autopay/customers').then(r => r.data as AutopayWho),
    { retry: false, staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false });

  const refresh = () => {
    qc.invalidateQueries('billing-autopay-who'); qc.invalidateQueries('autopay-summary');
    qc.invalidateQueries('billing-review'); qc.invalidateQueries('attention');
  };
  const setAutopay = useMutation(({ id, on }: { id: number; on: boolean }) => api.put(`/billing/autopay/customers/${id}`, { on }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Saved'); refresh(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not change autopay'); refresh(); },
  });
  const chargeAgain = useMutation((invoiceId: number) => api.post(`/billing/invoices/${invoiceId}/charge-card`).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Paid'); refresh(); qc.invalidateQueries('invoices'); qc.invalidateQueries('billing-dashboard'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'The card did not pay'); refresh(); },
  });

  if (!data) return null;
  const charged = data.charged || [];
  const cardOnly = data.card_only || [];
  const noCard = data.no_card || [];
  const total = charged.length + cardOnly.length + noCard.length;

  const row = (p: AutopayPerson, action: 'on' | 'off') => {
    const busy = setAutopay.isLoading && setAutopay.variables?.id === p.customer_id;
    return (
      <li key={p.customer_id} className="py-2 flex items-start justify-between gap-2">
        <div className="min-w-0 text-sm">
          <Link to={`/customers/${p.customer_id}`} className="font-semibold text-gray-900 hover:underline truncate block">{p.name}</Link>
          <p className="text-xs text-gray-600 truncate">{cardWords(p)}{p.phone ? ` · ${p.phone}` : ''}</p>
          <p className="text-xs text-gray-500">
            {p.last_charge
              ? `Last charged ${money(p.last_charge.amount)} · ${day(p.last_charge.paid_at)} · ${p.last_charge.invoice_number}`
              : (p.autopay_enabled ? 'Never charged yet' : 'Gets the normal bill')}
          </p>
          {p.open_decline && (
            <p className="text-xs text-red-700 mt-0.5">
              {p.open_decline.invoice_number} ({money(p.open_decline.balance)}{Number(p.open_decline.balance) !== Number(p.open_decline.total) ? ` still owed of ${money(p.open_decline.total)}` : ''}): {p.open_decline.error}
              <button type="button" disabled={chargeAgain.isLoading}
                onClick={() => { if (window.confirm(`Charge ${p.name}'s card on file ${money(p.open_decline!.balance)} for ${p.open_decline!.invoice_number} now?`)) chargeAgain.mutate(p.open_decline!.id); }}
                className="ml-2 px-2 py-0.5 rounded border border-red-300 bg-white text-red-800 font-semibold disabled:opacity-50">
                {chargeAgain.isLoading && chargeAgain.variables === p.open_decline.id ? 'Charging…' : 'Charge again'}
              </button>
            </p>
          )}
        </div>
        <button type="button" disabled={setAutopay.isLoading}
          onClick={() => {
            const q = action === 'on'
              ? `Turn autopay on for ${p.name}? Their card on file pays each month-end bill when you send it.`
              : `Turn autopay off for ${p.name}? They get the normal bill and pay it themselves.`;
            if (window.confirm(q)) setAutopay.mutate({ id: p.customer_id, on: action === 'on' });
          }}
          className={`flex-shrink-0 text-xs px-3 py-1.5 rounded-lg font-semibold border disabled:opacity-50 ${action === 'on' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300'}`}>
          {busy ? '…' : action === 'on' ? 'Turn on' : 'Turn off'}
        </button>
      </li>
    );
  };

  return (
    <div className="card p-4 space-y-3">
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-start justify-between gap-2 text-left">
        <div className="min-w-0 flex items-start gap-2">
          <CreditCard className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900">Autopay — {plural(charged.length, 'customer')}</p>
            <p className="text-xs text-gray-600">
              {charged.length ? `${charged.length === 1 ? 'Pays' : 'Pay'} the month-end bill with the card on file` : 'Nobody pays by card automatically yet'}
              {cardOnly.length ? ` · ${cardOnly.length} saved a card but not autopay` : ''}
              {noCard.length ? ` · ${noCard.length} chose autopay with no card` : ''}
            </p>
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <p className={`text-xs rounded-lg px-3 py-2 border ${data.charging ? 'text-emerald-800 bg-emerald-50 border-emerald-200' : 'text-amber-800 bg-amber-50 border-amber-200'}`}>
            {data.words}
          </p>

          {charged.length > 0 ? (
            <ul className="divide-y divide-gray-100 border-t border-b border-gray-100">
              {charged.map(p => row(p, 'off'))}
            </ul>
          ) : (
            <p className="text-xs text-gray-600">
              A customer turns it on on their own page (Credit card on file → Turn on); saving a card there turns it on for them.
              {cardOnly.length ? ' Or turn it on below for someone who already saved a card.' : ''}
              {!total ? ' Nobody has a card on file yet.' : ''}
            </p>
          )}

          {noCard.length > 0 && (
            <div className="space-y-1">
              <button type="button" onClick={() => setShowNoCard(s => !s)} className="text-xs font-semibold text-red-800 flex items-center gap-1">
                Chose autopay, no card at Stripe ({noCard.length})
                <ChevronDown className={`h-3 w-3 transition-transform ${showNoCard ? 'rotate-180' : ''}`} />
              </button>
              {showNoCard && (
                <>
                  <p className="text-xs text-gray-600">The card cannot pay — they get the normal bill until they save a card on their own page.</p>
                  <ul className="divide-y divide-gray-100 border-t border-b border-gray-100">{noCard.map(p => row(p, 'off'))}</ul>
                </>
              )}
            </div>
          )}

          {cardOnly.length > 0 && (
            <div className="space-y-1">
              <button type="button" onClick={() => setShowCardOnly(s => !s)} className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                Card saved, autopay off ({cardOnly.length})
                <ChevronDown className={`h-3 w-3 transition-transform ${showCardOnly ? 'rotate-180' : ''}`} />
              </button>
              {showCardOnly && (
                <>
                  <p className="text-xs text-gray-600">They get the normal bill and pay it themselves. Their page told them a saved card is billed automatically, so turning it on is what they expect.</p>
                  <ul className="divide-y divide-gray-100 border-t border-b border-gray-100">{cardOnly.map(p => row(p, 'on'))}</ul>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
